import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, ClickAwayListener, IconButton } from "@mui/material";
import FilterIcon from "../../common/components/filterPopover/filter-icon.svg?react";
import { useTranslation } from "../../localization/useTranslation";
import { useAiChatActions, useAiChatState } from "../AiChatContext";
import { useContractFilter } from "../../contract/ContractFilter.hooks";
import { ContractFilterRequest, transformFilterOptionsToBackend } from "../../contract/ContractFilter.types";
import { ContractFieldGroupingOrder, FilterOption } from "../../common/components/filterPopover/FilterPopover.types";
import FilterPopover from "../../common/components/filterPopover/FilterPopover";
import { useFilterBarData } from "../../common/components/filterBar/FilterBar.hooks";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { nextPinnedFilterIds, nextStoredFilterData, pinnedFilterIdsKey } from "@common/components/filterBar/filterPinning.ts";

interface AiChatScopePickerProps {
    conversationId: string | null;
}

function AiChatScopePicker({ conversationId }: AiChatScopePickerProps) {
    const { translateString } = useTranslation();
    const { getScope } = useAiChatState();
    const { setScope } = useAiChatActions();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [filterOpen, setFilterOpen] = useState(false);

    const scope = getScope(conversationId);
    const { contractFilterOptions, isLoading } = useContractFilter(scope?.contracts);
    const { data: filterBarData } = useFilterBarData(contractFilterOptions, !isLoading);
    const pinnedFilterIds = scope?.contracts?.pinnedFilterIds;

    function handleFilterClose(updatedOptions: Array<FilterOption>, menuPinnedFilterIds?: Array<string>) {
        const newFilter = transformFilterOptionsToBackend(updatedOptions);
        const newPinnedFilterIds = nextPinnedFilterIds(contractFilterOptions, menuPinnedFilterIds ?? pinnedFilterIds, updatedOptions);
        const contracts = nextStoredFilterData(newFilter, newPinnedFilterIds) as Partial<ContractFilterRequest>;

        // A scope that is nothing but pins still has to persist: the empty chips are the user's half-finished scope.
        const scopeIsEmpty = isEmptyFilter(newFilter) && newPinnedFilterIds === undefined;
        setScope(conversationId, scopeIsEmpty ? undefined : { contracts });
        setFilterOpen(false);
    }

    return (
        <>
            <DotLegalTooltip text={translateString("aiChatAddScope")}>
                <IconButton
                    ref={buttonRef}
                    onClick={() => setFilterOpen(true)}
                    size="small"
                    sx={{
                        "& svg": { width: 18, height: 18 },
                    }}
                >
                    <FilterIcon />
                </IconButton>
            </DotLegalTooltip>

            {filterOpen &&
                createPortal(
                    <ClickAwayListener onClickAway={() => handleFilterClose(contractFilterOptions)}>
                        <Box>
                            <FilterPopover
                                isLoading={isLoading}
                                filterData={contractFilterOptions}
                                filterBarData={filterBarData}
                                groupingOrder={ContractFieldGroupingOrder}
                                pinnedFilterIds={pinnedFilterIds}
                                onMenuClose={handleFilterClose}
                                open={filterOpen}
                                anchorEl={buttonRef.current}
                                onOpenChange={setFilterOpen}
                                submenuPlacement="left-start"
                            />
                        </Box>
                    </ClickAwayListener>,
                    document.body
                )}
        </>
    );
}

// True when no user-set filter dimension carries a meaningful value. Pagination / sort
// fields are output-shaping defaults set by transformFilterOptionsToBackend even when no
// real filter is configured, so they don't count toward "non-empty". Pins are presentation
// state for the chips, so they don't either.
function isEmptyFilter(filter: Partial<ContractFilterRequest>): boolean {
    const ignored = new Set(["pageNumber", "pageSize", "sortByColumn", "sortDirection", "sortByCustomFieldId", pinnedFilterIdsKey]);
    return !Object.entries(filter).some(([key, value]) => {
        if (ignored.has(key) || value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "string") return value.trim().length > 0;
        if (typeof value === "object") {
            if ("value" in value) {
                const inner = (value as { value: unknown }).value;
                if (Array.isArray(inner)) return inner.length > 0;
                if (typeof inner === "string") return inner.trim().length > 0;
                return inner !== null && inner !== undefined;
            }
            return Object.keys(value).length > 0;
        }
        return Boolean(value);
    });
}

export default AiChatScopePicker;
